import React, { useEffect, useState } from 'react';
import { Radio, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getSession, subscribe, startTracking } from '../native/backgroundTracker';

const dismissKey = (loadId) => `civilx_share_prompt_dismissed_${loadId}`;

// Shown when the driver's load is In Transit and no tracking session is running.
// One tap starts the explicit session (in-context permission prompt) — nothing starts on its own.
export const ShareLocationPrompt = ({ load }) => {
  const [sess, setSess] = useState(getSession());
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const loadId = load?.id || load?._id;

  useEffect(() => subscribe(setSess), []);
  useEffect(() => {
    try { setDismissed(!!loadId && sessionStorage.getItem(dismissKey(loadId)) === '1'); } catch { setDismissed(false); }
  }, [loadId]);

  if (!load || load.status !== 'in_transit' || sess.active || dismissed) return null;

  const accept = async () => {
    setBusy(true);
    const res = await startTracking();
    setBusy(false);
    if (res.ok) toast.success('Sharing your live location with dispatch');
    else if (res.reason === 'denied') toast.error('Location permission is required — allow it for CivilX in Settings, then try again.');
    else toast.error(sess.status || 'Could not start location sharing');
  };
  const dismiss = () => {
    try { sessionStorage.setItem(dismissKey(loadId), '1'); } catch { /* storage blocked */ }
    setDismissed(true);
  };

  return (
    <div data-testid="share-location-prompt" className="mb-4 bg-[#002FA7] text-white rounded-xl p-4 shadow-md" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
          <Radio className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm" style={{ fontFamily: 'Work Sans, sans-serif' }}>Location sharing</p>
          <p className="text-xs text-white/80 mt-0.5">
            CivilX TMS can use your location during an active load to provide your authorized dispatcher with trip progress and estimated arrival information. Location sharing can be stopped when the trip is completed or when you stop sharing. Tap Continue to allow location access.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" onClick={accept} disabled={busy} data-testid="share-location-accept"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white text-[#002FA7] text-sm font-bold hover:bg-blue-50 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radio className="w-4 h-4" />} Continue
            </button>
            <button type="button" onClick={dismiss} data-testid="share-location-dismiss"
                    className="px-3 py-2 rounded-md text-sm font-semibold text-white/90 hover:bg-white/10">
              Not now
            </button>
          </div>
        </div>
        <button type="button" onClick={dismiss} aria-label="Dismiss" className="p-1 rounded hover:bg-white/10"><X className="w-4 h-4" /></button>
      </div>
    </div>
  );
};
